import { useTranslation } from 'next-i18next';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Button } from '@hotelplan/components.common.buttons';
import { Icon } from '@hotelplan/components.common.icon';
import { Tooltip } from '@hotelplan/components.common.tooltip';
import { Flight, OrlFlightAlternative } from '@hotelplan/graphql.types';
import { useDeviceType } from '@hotelplan/libs.context.device-type';
import { useToggleState } from '@hotelplan/libs.hooks-react';
import { sx2CssThemeFn } from '@hotelplan/libs.sx';
import { formatPrice } from '@hotelplan/libs.utils';
import { PriceFragment } from 'graphql/fragments/Price.generated';
import FlightStopsOverlay from './FlightStopsOverlay';
import OfferPriceDescription, { TooltipWrapper } from './OfferPriceDescription';

const OPEN_FLIGHT_MODAL_EVENT = 'open-flight-modal-event';

const AlternativeFlightItemWrap = styled.div.attrs({
  className: 'column',
})(
  sx2CssThemeFn({
    display: 'grid',
    gridTemplateColumns: ['repeat(3, 1fr)', '15% 26% 23% 18% 18%'],
    alignItems: 'start',
    gap: [null, '5px 0px'],
    pt: '10px',
    pb: [1, '10px'],
    pl: ['10px', 2],
    pr: ['10px', 0],
    borderBottom: ['1px solid #c2c2c2', 'unset'],
    '.button': {
      variant: 'buttons.allOffers',
      '.icon': {
        ml: 'auto',
      },
      width: ['100%', 'auto'],
    },
    '.transfer-wrap': {
      display: [null, 'flex'],
      alignItems: [null, 'end'],
    },
    '.transfer': {
      gridColumn: ['span 3', 'unset'],
      display: 'flex',
      alignItems: ['center', 'start'],
      pb: [null, 1],
      gap: '6px',
      '.text': {
        fontSize: ['12px', '11px'],
        color: ['textGray', '#000'],
        pt: [null, '2px'],
      },
    },
    '& > div': {
      borderBottom: [null, '1px solid #c2c2c2'],
      height: '100%',
      '&:empty': {
        borderColor: 'transparent',
      },
    },
    '.open-overlay': {
      position: 'relative',
      backgroundColor: 'primary',
      color: 'white',
      borderRadius: '50%',
      p: '1px',
      ml: 1,
      '.icon': {
        width: ['15px', '11px'],
        height: ['15px', '11px'],
      },
    },
    '.departure-details': {
      display: 'flex',
      alignItems: 'start',
      '.flight-stops-overlay-wrapper': {
        position: 'relative',
        display: 'flex',
      },
    },
  })
);

const FlightSectionWrap = styled.div(
  sx2CssThemeFn({
    '.time': {
      color: 'textGray',
      fontSize: ['12px', '14px'],
      fontWeight: 'bold',
      mb: 1,
      display: 'flex',
      alignItems: 'center',
    },
    '.stops': {
      color: 'textGray',
      fontSize: ['11px', '12px'],
      fontWeight: ['bold', 'normal'],
      mb: 1,
    },
    '.open-overlay': {
      position: 'relative',
      backgroundColor: 'primary',
      color: 'white',
      borderRadius: '50%',
      p: '1px',
      ml: 1,
      '.icon': {
        width: '11px',
        height: '11px',
      },
    },
    '.airline-logo': {
      maxHeight: '25px',
    },
  })
);

function FlightSection(props: Flight) {
  const [t] = useTranslation('orl');

  const { departureTime, arrivalTime, segments } = props;
  const stopsCount = segments ? segments.length - 1 : 0;

  return (
    <FlightSectionWrap>
      <div className="time">
        {departureTime} - {arrivalTime}
      </div>

      <div className="stops">
        {!stopsCount
          ? t('flight.direct')
          : t('stops.count', {
              count: stopsCount,
            })}
      </div>

      <img
        className="airline-logo"
        src={segments[0].airline.airlineLogo}
        alt={segments[0].airline.name}
      />
    </FlightSectionWrap>
  );
}

interface IAlternativeFlightItem {
  flight: OrlFlightAlternative;
  transferPrice: PriceFragment;
  onCheckout: (alternativeFlight?: OrlFlightAlternative) => void;
  transferIncluded: boolean;
}

function AlternativeFlightItem({
  flight,
  transferPrice,
  transferIncluded,
  onCheckout,
}: IAlternativeFlightItem) {
  const [t] = useTranslation(['common', 'orl']);
  const { mobile } = useDeviceType();
  const [showTooltip, open, close] = useToggleState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const [
    showFlightSectionOverlayModal,
    openFlightSectionOverlayModal,
    closeFlightSectionOverlayModal,
  ] = useToggleState(false);

  const showFlightDetails =
    flight.inboundFlight.segments.length > 0 ||
    flight.outboundFlight.segments.length > 0;

  const handleOpenFlightModal = event => {
    if (event.detail !== flight.offerId) {
      closeFlightSectionOverlayModal();
    }
  };

  useEffect(() => {
    if (showFlightSectionOverlayModal) {
      window.addEventListener(OPEN_FLIGHT_MODAL_EVENT, handleOpenFlightModal);
    }

    return () => {
      window.removeEventListener(
        OPEN_FLIGHT_MODAL_EVENT,
        handleOpenFlightModal
      );
    };
  }, [showFlightSectionOverlayModal]);

  return (
    <AlternativeFlightItemWrap>
      {!mobile ? (
        <>
          <div className="column"></div>
          <div className="transfer-wrap column">
            <div className="transfer">
              {transferPrice || transferIncluded ? (
                <>
                  <div className="icon-wrapper">
                    <Icon name="transfer" size="xs" />
                  </div>

                  <div className="text">
                    {transferIncluded ? t('orl:offer.transferIncluded') : null}

                    {transferPrice ? (
                      <>
                        {t('orl:offer.transfer')} {`${t('common:priceFrom')}`}
                        <br />{' '}
                        {`${formatPrice(transferPrice)} ${t(
                          'common:transfer.perPerson'
                        )}`}
                      </>
                    ) : null}
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </>
      ) : null}
      <div className="departure-details">
        <FlightSection {...flight.outboundFlight} />
        {showFlightDetails ? (
          <div className="flight-stops-overlay-wrapper">
            <Button
              className="open-overlay"
              icon={{
                name: 'plus',
              }}
              variant="iconBtn"
              onClick={() => {
                openFlightSectionOverlayModal();
                scroll();
                if (typeof window !== 'undefined') {
                  window.dispatchEvent(
                    new CustomEvent(OPEN_FLIGHT_MODAL_EVENT, {
                      detail: flight.offerId,
                    })
                  );
                }
              }}
            >
              Open
            </Button>
            {showFlightSectionOverlayModal ? (
              <FlightStopsOverlay
                onClose={closeFlightSectionOverlayModal}
                flight={flight}
                // refValue={refValue}
              />
            ) : null}
          </div>
        ) : null}
      </div>
      <FlightSection {...flight.inboundFlight} />
      <div>
        <TooltipWrapper onMouseEnter={open} onMouseLeave={close}>
          <Button
            className="button"
            icon={{
              name: 'chevron-long-right',
              size: 'xs',
            }}
            onMouseEnter={open}
            onMouseLeave={close}
            onClick={() => {
              onCheckout(flight);
            }}
          >
            {mobile
              ? formatPrice(flight.pricePerPerson)
              : formatPrice(flight.totalPrice)}
          </Button>
          {showTooltip && !mobile ? (
            <Tooltip tooltipElRef={tooltipRef} withAutoPositioning>
              <OfferPriceDescription pricePerPerson={flight.pricePerPerson} />
            </Tooltip>
          ) : null}
        </TooltipWrapper>
      </div>

      {mobile && (transferPrice || transferIncluded) ? (
        <div className="transfer">
          <div className="icon-wrapper">
            <Icon name="transfer" size="xs" />
          </div>
          <div className="text">
            {transferIncluded ? t('orl:offer.transferIncluded') : null}
            {transferPrice ? (
              <>
                {t('orl:offer.transfer')} {`${t('common:priceFrom')}`}
                <br />{' '}
                {`${formatPrice(transferPrice)} ${t(
                  'common:transfer.perPerson'
                )}`}
              </>
            ) : null}
          </div>
        </div>
      ) : null}
    </AlternativeFlightItemWrap>
  );
}

export default React.memo(AlternativeFlightItem);
